import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Ban, Play, Trash2, Search } from "lucide-react";
import { T } from "@/lib/testIds";

const roleTone = {
  platform_admin: "bg-[#0A0A0B] text-white border-transparent",
  owner: "bg-[#ECECFE] text-[#4338CA] border-[#C7D2FE]",
  admin: "bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]",
  staff: "bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]",
};

export default function PlatformUsers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "owner",
    business_name: "", business_type: "collector",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (role !== "all") params.role = role;
      const r = await api.get("/platform/users", { params });
      setRows(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, role]);

  const toggleSuspend = async (u) => {
    setBusy(u.id);
    try {
      await api.patch(`/platform/users/${u.id}/suspend`, null, { params: { suspended: !u.suspended } });
      toast.success(!u.suspended ? "User suspended" : "User reactivated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}?`)) return;
    setBusy(u.id);
    try {
      await api.delete(`/platform/users/${u.id}`);
      toast.success("User deleted");
      setRows((r) => r.filter((x) => x.id !== u.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(null); }
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, email: form.email.toLowerCase() };
      if (form.role === "platform_admin") {
        delete payload.business_name;
        delete payload.business_type;
      }
      await api.post("/platform/users", payload);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "owner", business_name: "", business_type: "collector" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="co-label">Platform console</div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mt-1">Users</h1>
        </div>
        <button
          data-testid={T.platformCreateUserBtn}
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press transition-colors duration-200"
        >
          <UserPlus className="h-4 w-4" strokeWidth={2} />
          Create user
        </button>
      </header>

      <div className="p-6 lg:p-10 space-y-4">
        <div className="border co-hairline bg-white p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" strokeWidth={1.75} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9 h-10" />
          </div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-[200px] h-10"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="platform_admin">Platform admin</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="staff">Staff</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border co-hairline bg-white overflow-x-auto">
          <table className="co-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Workspace</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center text-[#52525B]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-[#52525B] py-10">No users match.</td></tr>
              ) : rows.map((u) => (
                <tr key={u.id} data-testid={T.platformUserRow}>
                  <td className="font-semibold">{u.name}</td>
                  <td className="text-[#52525B]">{u.email}</td>
                  <td>
                    <span className={`co-pill border ${roleTone[u.role] || roleTone.staff}`}>
                      {u.role.replace("_", " ").toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className="text-sm">{u.workspace?.name || "—"}</div>
                    {u.workspace?.suspended && <span className="text-xs text-[#B45309] font-semibold">workspace suspended</span>}
                  </td>
                  <td>
                    <span className={u.suspended ? "co-pill co-pill-overdue" : "co-pill co-pill-paid"}>
                      {u.suspended ? "SUSPENDED" : "ACTIVE"}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        disabled={busy === u.id}
                        onClick={() => toggleSuspend(u)}
                        data-testid={`${u.suspended ? T.platformUnsuspendBtn : T.platformSuspendBtn}-${u.id}`}
                        className="p-2 hover:bg-[#F4F4F5] rounded-sm transition-colors duration-200"
                        title={u.suspended ? "Reactivate" : "Suspend"}
                      >
                        {u.suspended
                          ? <Play className="h-4 w-4 text-[#15803D]" strokeWidth={1.75} />
                          : <Ban className="h-4 w-4 text-[#B45309]" strokeWidth={1.75} />}
                      </button>
                      <button
                        disabled={busy === u.id}
                        onClick={() => remove(u)}
                        data-testid={`${T.platformDeleteBtn}-${u.id}`}
                        className="p-2 hover:bg-[#FEF2F2] rounded-sm transition-colors duration-200"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-[#B91C1C]" strokeWidth={1.75} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Create user</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label className="co-label">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Workspace owner (needs business)</SelectItem>
                  <SelectItem value="platform_admin">Platform admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="co-label">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-2" />
              </div>
              <div>
                <Label className="co-label">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-2" />
              </div>
            </div>
            <div>
              <Label className="co-label">Temporary password</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={6} required className="mt-2 font-mono" placeholder="At least 6 characters" />
            </div>
            {form.role === "owner" && (
              <>
                <div>
                  <Label className="co-label">Business name</Label>
                  <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} required className="mt-2" placeholder="e.g. Sharma Coaching" />
                </div>
                <div>
                  <Label className="co-label">Business type</Label>
                  <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
                    <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="collector">Payment collector (School, Coaching, Gym, SaaS…)</SelectItem>
                      <SelectItem value="distributor">Financial distributor (Insurance / MF)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm font-semibold border border-[#0A0A0B] rounded-sm co-btn-press hover:bg-[#F4F4F5]">Cancel</button>
              <button
                type="submit" disabled={saving}
                data-testid={T.platformCreateUserSubmit}
                className="px-4 py-2 text-sm font-semibold bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-sm co-btn-press disabled:opacity-60 transition-colors duration-200"
              >
                {saving ? "Creating…" : "Create user"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
